/**
 * Felles skjemafelt for familiekode, brukernavn, passord og kjøkken-PIN
 * (#141, FF-09 #177).
 *
 * Brukes av innloggingen, innstillingene, medlemsskjemaet, onboarding,
 * invitasjonen og tilbakestillingslenken, så feltene ser og oppfører seg likt
 * overalt. Utseendet følger skissene i design/ (#159, steg 9g, Stell_Innlogging):
 * `field`-klassen (52 px, radius 16) og etiketter i versaler som SectionLabel.
 *
 * Validering bruker de delte Zod-skjemaene (`Username`, `Password`) — ingen
 * egne regler på klienten. Sjekkene mot brukernavnet og lista over vanlige
 * passord gjør serveren; de kommer tilbake som `weak_password` + `reason`.
 */
import { useId, useState } from 'react';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import {
  FAMILY_CODE_MAX,
  PASSWORD_MAX,
  Password,
  PinSetBody,
  USERNAME_MAX,
  Username,
} from '@stell/shared';
import { PinKeypad } from '../../components/PinKeypad';
import { Button } from '../../components/primitives';
import { SectionLabel } from '../../components/primitives/SectionLabel';
import { weakPasswordMessage } from './authApi';

const INPUT_CLASS = 'field';
// Etikett over feltet med 6 px luft, som `lbl` + `field` i skissene.
const FIELD_WRAP = 'flex flex-col gap-1.5';

interface FieldMessageProps {
  id: string;
  hint?: string | null | undefined;
  error?: string | null | undefined;
  testId: string;
}

/** Hjelpetekst eller feil under et felt; feilen vinner. */
function FieldMessage({ id, hint, error, testId }: FieldMessageProps): ReactElement | null {
  if (error) {
    return (
      <span id={id} data-testid={`${testId}-error`} className="text-bad text-sm">
        {error}
      </span>
    );
  }
  if (hint) {
    return (
      <span id={id} className="text-ink-2 text-sm">
        {hint}
      </span>
    );
  }
  return null;
}

// ─── Brukernavn ─────────────────────────────────────────────────────────────

/** Feilkode fra `Username`-skjemaet, eller null når feltet er tomt/gyldig. */
export function usernameError(raw: string): 'invalid_username' | null {
  if (raw.trim() === '') return null;
  return Username.safeParse(raw).success ? null : 'invalid_username';
}

interface UsernameFieldProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  testId: string;
  hint?: string | undefined;
  /** Serverfeil (allerede oversatt) som vises i stedet for klientvalideringen. */
  error?: string | null | undefined;
  /** `off` når en admin skriver brukernavnet til noen andre. */
  autoComplete?: 'username' | 'off';
  autoFocus?: boolean;
  /** Vis formatfeil fra `Username` mens man skriver (av for innloggingen, se LoginBody). */
  validate?: boolean;
}

export function UsernameField({
  label,
  value,
  onChange,
  testId,
  hint,
  error,
  autoComplete = 'username',
  autoFocus = false,
  validate = true,
}: UsernameFieldProps): ReactElement {
  const { t } = useTranslation();
  const id = useId();
  const msgId = `${id}-msg`;
  const shown = error ?? (validate && usernameError(value) ? t('auth.error.invalid_username') : null);
  return (
    <div className={FIELD_WRAP}>
      <SectionLabel as="label" htmlFor={id} flush>
        {label}
      </SectionLabel>
      <input
        id={id}
        data-testid={testId}
        className={INPUT_CLASS}
        type="text"
        name="username"
        autoComplete={autoComplete}
        autoCapitalize="none"
        autoCorrect="off"
        spellCheck={false}
        autoFocus={autoFocus}
        maxLength={USERNAME_MAX}
        value={value}
        aria-invalid={shown ? true : undefined}
        aria-describedby={shown || hint ? msgId : undefined}
        onChange={(e) => onChange(e.target.value)}
      />
      <FieldMessage id={msgId} hint={hint} error={shown} testId={testId} />
    </div>
  );
}

// ─── Familiekode (FF-09, #177) ──────────────────────────────────────────────

interface FamilyCodeFieldProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  testId: string;
  hint?: string | undefined;
  autoFocus?: boolean;
}

/**
 * Familiekoden på innloggingen. Valideres ikke mens man skriver: en kode med
 * feil form skal gi samme svar som en ukjent (se LoginBody).
 */
export function FamilyCodeField({
  label,
  value,
  onChange,
  testId,
  hint,
  autoFocus = false,
}: FamilyCodeFieldProps): ReactElement {
  const id = useId();
  const msgId = `${id}-msg`;
  return (
    <div className={FIELD_WRAP}>
      <SectionLabel as="label" htmlFor={id} flush>
        {label}
      </SectionLabel>
      <input
        id={id}
        data-testid={testId}
        className={INPUT_CLASS}
        type="text"
        name="family"
        autoComplete="organization"
        autoCapitalize="none"
        autoCorrect="off"
        spellCheck={false}
        autoFocus={autoFocus}
        maxLength={FAMILY_CODE_MAX}
        value={value}
        aria-describedby={hint ? msgId : undefined}
        onChange={(e) => onChange(e.target.value)}
      />
      <FieldMessage id={msgId} hint={hint} testId={testId} />
    </div>
  );
}

// ─── Passord ────────────────────────────────────────────────────────────────

interface PasswordFieldProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  autoComplete: 'current-password' | 'new-password';
  testId: string;
  hint?: string | null | undefined;
  error?: string | null | undefined;
  autoFocus?: boolean;
}

/** Passordfelt med «Vis/Skjul»-knapp. */
export function PasswordField({
  label,
  value,
  onChange,
  autoComplete,
  testId,
  hint,
  error,
  autoFocus = false,
}: PasswordFieldProps): ReactElement {
  const { t } = useTranslation();
  const id = useId();
  const msgId = `${id}-msg`;
  const [visible, setVisible] = useState(false);
  return (
    <div className={FIELD_WRAP}>
      <SectionLabel as="label" htmlFor={id} flush>
        {label}
      </SectionLabel>
      <div className="relative">
        <input
          id={id}
          data-testid={testId}
          className={`${INPUT_CLASS} pr-20`}
          type={visible ? 'text' : 'password'}
          autoComplete={autoComplete}
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          autoFocus={autoFocus}
          maxLength={PASSWORD_MAX}
          value={value}
          aria-invalid={error ? true : undefined}
          aria-describedby={error || hint ? msgId : undefined}
          onChange={(e) => onChange(e.target.value)}
        />
        <button
          type="button"
          data-testid={`${testId}-toggle`}
          className="text-ink absolute top-1 right-1 h-11 rounded-md px-3 text-control font-extrabold"
          aria-pressed={visible}
          aria-controls={id}
          onClick={() => setVisible((v) => !v)}
        >
          {visible ? t('auth.hide') : t('auth.show')}
          {/* Tilgjengelig navn: «Vis passord» / «Skjul passord». */}
          <span className="sr-only"> {t('auth.password')}</span>
        </button>
      </div>
      <FieldMessage id={msgId} hint={hint} error={error} testId={testId} />
    </div>
  );
}

// ─── Nytt passord + bekreft ────────────────────────────────────────────────

export interface NewPasswordValue {
  password: string;
  confirm: string;
}

export const EMPTY_NEW_PASSWORD: NewPasswordValue = { password: '', confirm: '' };

/** Gyldig etter `Password` og likt i begge felt. */
export function isNewPasswordValid(v: NewPasswordValue): boolean {
  return Password.safeParse(v.password).success && v.password === v.confirm;
}

interface NewPasswordFieldsProps {
  value: NewPasswordValue;
  onChange: (value: NewPasswordValue) => void;
  /** testId for passordfeltet; bekreftfeltet får `${testId}-confirm`. */
  testId: string;
  label?: string;
  /** Serverfeil (allerede oversatt), vises under passordfeltet. */
  error?: string | null | undefined;
}

export function NewPasswordFields({
  value,
  onChange,
  testId,
  label,
  error,
}: NewPasswordFieldsProps): ReactElement {
  const { t } = useTranslation();
  // For kort vises først når man har gått videre til bekreftfeltet — ikke
  // mens man fortsatt skriver det første tegnet. (For langt hindrer maxLength.)
  const parsed = Password.safeParse(value.password);
  const lengthError =
    value.confirm !== '' && !parsed.success
      ? weakPasswordMessage(t, parsed.error.issues[0]?.message)
      : null;
  const mismatch = value.confirm !== '' && value.confirm !== value.password;
  return (
    <>
      <PasswordField
        label={label ?? t('auth.new_password')}
        value={value.password}
        onChange={(password) => onChange({ ...value, password })}
        autoComplete="new-password"
        testId={testId}
        hint={t('auth.password_hint')}
        error={error ?? lengthError}
      />
      <PasswordField
        label={t('auth.confirm_password')}
        value={value.confirm}
        onChange={(confirm) => onChange({ ...value, confirm })}
        autoComplete="new-password"
        testId={`${testId}-confirm`}
        error={mismatch ? t('auth.password_mismatch') : null}
      />
    </>
  );
}

// ─── Kjøkken-PIN (valgfri) ──────────────────────────────────────────────────

export interface PinPairValue {
  pin: string;
  pinConfirm: string;
}

export const EMPTY_PIN_PAIR: PinPairValue = { pin: '', pinConfirm: '' };

/** `null` (ingen PIN valgt) er gyldig; ellers 4–8 sifre og likt i begge. */
export function isPinPairValid(v: PinPairValue | null): boolean {
  // PIN-regelen kommer fra den delte kontrakten, ikke en egen regex.
  return v === null || (PinSetBody.shape.pin.safeParse(v.pin).success && v.pin === v.pinConfirm);
}

interface OptionalPinKeypadsProps {
  /** `null` = ingen PIN; tastaturene er skjult bak en knapp. */
  value: PinPairValue | null;
  onChange: (value: PinPairValue | null) => void;
  /** Prefiks for testId-ene; bekreftet får `${prefix}-confirm`. */
  testIdPrefix?: string;
  /** Tekst og testId på knappen som viser tastaturene. */
  addLabel?: string | undefined;
  addTestId?: string;
}

/**
 * PIN + bekreft for hurtigbyttet på kjøkkenskjermen, skjult til man ber om
 * den — PIN er valgfri etter #141. Var kopiert i StepMember, MemberForm og
 * MemberInviteRoute før.
 */
export function OptionalPinKeypads({
  value,
  onChange,
  testIdPrefix = 'pin',
  addLabel,
  addTestId = `${testIdPrefix}-add`,
}: OptionalPinKeypadsProps): ReactElement {
  const { t } = useTranslation();
  if (value === null) {
    return (
      <Button
        variant="secondary"
        className="self-start"
        onClick={() => onChange(EMPTY_PIN_PAIR)}
        data-testid={addTestId}
      >
        {addLabel ?? t('auth.add_kitchen_pin')}
      </Button>
    );
  }
  const mismatch = value.pinConfirm !== '' && value.pin !== value.pinConfirm;
  return (
    <div className="flex flex-col gap-4">
      <p className="text-ink-2 text-sm">{t('auth.kitchen_pin_hint')}</p>
      <div className="flex flex-col gap-2">
        <SectionLabel as="span" flush>
          {t('onboarding.step3.pin')}
        </SectionLabel>
        <PinKeypad
          length={4}
          testIdPrefix={testIdPrefix}
          label={t('onboarding.step3.pin')}
          onChange={(pin) => onChange({ ...value, pin })}
        />
      </div>
      <div className="flex flex-col gap-2">
        <SectionLabel as="span" flush>
          {t('onboarding.step3.pin_confirm')}
        </SectionLabel>
        <PinKeypad
          length={4}
          testIdPrefix={`${testIdPrefix}-confirm`}
          label={t('onboarding.step3.pin_confirm')}
          onChange={(pinConfirm) => onChange({ ...value, pinConfirm })}
        />
        {mismatch ? (
          <span data-testid={`${testIdPrefix}-mismatch`} className="text-bad text-sm">
            {t('onboarding.step3.pin_mismatch')}
          </span>
        ) : null}
      </div>
      <Button
        variant="ghost"
        className="self-start"
        onClick={() => onChange(null)}
        data-testid={`${testIdPrefix}-remove`}
      >
        {t('auth.remove_kitchen_pin')}
      </Button>
    </div>
  );
}

/**
 * Skjult brukernavn i skjemaer som bare ber om passord, så passordbehandlere
 * lagrer det nye passordet på riktig konto.
 */
export function HiddenUsername({ value }: { value: string | null | undefined }): ReactElement | null {
  if (!value) return null;
  return <input type="text" name="username" autoComplete="username" value={value} readOnly hidden />;
}

interface PinInputProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  testId: string;
  error: string | null;
}

/**
 * Maskert PIN-felt for skjemaer med tekstfelt (Innstillinger). Bare sifre;
 * mobilen får talltastatur via inputMode. PIN-tastaturet passer bedre på
 * kjøkkenskjermen, et vanlig felt bedre i et passordskjema på telefonen.
 */
export function PinInput({ label, value, onChange, testId, error }: PinInputProps): ReactElement {
  const id = useId();
  const msgId = `${id}-msg`;
  return (
    <div className={FIELD_WRAP}>
      <SectionLabel as="label" htmlFor={id} flush>
        {label}
      </SectionLabel>
      <input
        id={id}
        data-testid={testId}
        className={`${INPUT_CLASS} tnum font-mono`}
        type="password"
        inputMode="numeric"
        pattern="[0-9]*"
        autoComplete="off"
        maxLength={8}
        value={value}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? msgId : undefined}
        onChange={(e) => onChange(e.target.value.replace(/\D/g, ''))}
      />
      <FieldMessage id={msgId} error={error} testId={testId} />
    </div>
  );
}
